import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Flag,
  ChevronLeft,
  ChevronRight,
  Plus,
  AlertCircle,
  ExternalLink,
  Edit,
  MoreVertical,
  ClipboardList,
  Trophy,
  Timer,
  Send,
  List,
  Trash2,
  X,
  GripVertical,
  Printer,
  Copy,
  Check,
  Wifi,
  WifiOff,
  Radio,
  RefreshCw,
  Play,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { useApi } from '../../contexts/ApiContext';
import useLiveRace from '../../hooks/useLiveRace';

// Status configurations
const eventStatusConfig = {
  'Published': { variant: 'primary', color: 'text-wrg-blue-600' },
  'Draft': { variant: 'neutral', color: 'text-slate-500' },
  'Live': { variant: 'warning', color: 'text-amber-600' },
  'Completed': { variant: 'neutral', color: 'text-slate-500' },
  'Cancelled': { variant: 'error', color: 'text-red-600' },
  'Rescheduled': { variant: 'warning', color: 'text-amber-600' },
  'Pending Reschedule': { variant: 'warning', color: 'text-amber-600' },
};

const raceTypeColors = {
  'Heat': 'bg-blue-100 text-blue-700',
  'Qualifier': 'bg-purple-100 text-purple-700',
  'B-Main': 'bg-amber-100 text-amber-700',
  'A-Main': 'bg-emerald-100 text-emerald-700',
  'Feature': 'bg-emerald-100 text-emerald-700',
  'Last Chance': 'bg-orange-100 text-orange-700',
};

function QuickAction({ icon: Icon, label, to, onClick, variant = 'default' }) {
  const content = (
    <div className={`flex flex-col items-center gap-1.5 p-3 rounded border transition-colors cursor-pointer
      ${variant === 'primary'
        ? 'bg-wrg-blue-50 hover:bg-wrg-blue-100 text-wrg-blue-700 border-wrg-blue-200'
        : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200'}`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-xs font-medium text-center">{label}</span>
    </div>
  );

  if (to) {
    return <Link to={to}>{content}</Link>;
  }
  return <button onClick={onClick}>{content}</button>;
}

function RaceCard({ race, seriesSlug }) {
  const typeColor = raceTypeColors[race.race_type] || 'bg-slate-100 text-slate-600';

  return (
    <Link
      to={`/${seriesSlug}/races/${race.id}/lineup`}
      className="block p-2.5 rounded bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-wrg-blue-300 transition-colors group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`px-1.5 py-0.5 rounded text-2xs font-medium ${typeColor}`}>
            {race.race_type || 'Race'}
          </div>
          <div>
            <h4 className="text-sm font-medium text-slate-800 group-hover:text-wrg-blue-600 transition-colors">
              {race.name}
            </h4>
            <div className="flex items-center gap-2.5 mt-0.5 text-2xs text-slate-500">
              {race.start_local_time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {race.start_local_time}
                </span>
              )}
              {race.total_laps > 0 && (
                <span>{race.total_laps} laps</span>
              )}
              {race.series_name && (
                <span className="text-slate-400">{race.series_name}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="text-right">
            <div className="text-xs font-medium text-slate-700">
              {race.entry_count || 0} entries
            </div>
            {race.lineup_set ? (
              <Badge variant="success" size="sm">Lineup Set</Badge>
            ) : (
              <Badge variant="neutral" size="sm">No Lineup</Badge>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-wrg-blue-600" />
        </div>
      </div>
    </Link>
  );
}

// OOE Component Item
function shortTimezoneLabel(timeZone, refDateString) {
  if (!timeZone) return '';
  try {
    const refDate = refDateString ? new Date(`${refDateString}T12:00:00Z`) : new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(refDate);
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

// Format a plain TIME string ("HH:MM[:SS]") as 12-hour "h:mm AM/PM"
function formatTime12Hour(timeString) {
  if (!timeString) return null;
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  if (isNaN(hour)) return null;
  const min = minutes || '00';
  if (hour === 0) return `12:${min} AM`;
  if (hour < 12) return `${hour}:${min} AM`;
  if (hour === 12) return `12:${min} PM`;
  return `${hour - 12}:${min} PM`;
}

function parseDurationToMinutes(duration) {
  if (!duration) return 0;
  if (!isNaN(duration)) return parseInt(duration, 10);
  const parts = duration.split(':').map(p => parseInt(p, 10));
  if (parts.length === 3) return parts[0] * 60 + parts[1];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

function parseTimeFromDateTime(dateTimeStr) {
  if (!dateTimeStr) return null;
  const timePart = dateTimeStr.includes('T')
    ? dateTimeStr.split('T')[1]
    : dateTimeStr.split(' ')[1];
  if (!timePart) return null;
  const [h, m] = timePart.split(':').map(Number);
  return h * 60 + m;
}

function datePart(dateTimeStr) {
  if (!dateTimeStr) return '';
  return dateTimeStr.includes('T')
    ? dateTimeStr.split('T')[0]
    : dateTimeStr.split(' ')[0];
}

function addMinutesToDateTime(dateTimeStr, minutes) {
  const date = datePart(dateTimeStr);
  const timeMinutes = parseTimeFromDateTime(dateTimeStr);
  if (timeMinutes === null) return dateTimeStr;

  const total = timeMinutes + minutes;
  const dayOffset = Math.floor(total / 1440);
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;

  if (dayOffset !== 0) {
    const [y, mo, d] = date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, mo - 1, d + dayOffset));
    const newDate = dt.toISOString().split('T')[0];
    return `${newDate} ${time}`;
  }

  return `${date} ${time}`;
}

function recalculateTimes(components, fromIndex) {
  if (!components.length || fromIndex <= 0) return components;

  const result = components.map(c => ({ ...c }));
  for (let i = fromIndex; i < result.length; i++) {
    const prev = result[i - 1];
    if (!prev.start_date_time) continue;

    const durationMin = parseDurationToMinutes(prev.duration);
    result[i].start_date_time = addMinutesToDateTime(prev.start_date_time, durationMin);
  }
  return result;
}

function SortableOOEItem({ component, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: component.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const isRace = !!component.race_type_ID;

  const formatTime = (dateTimeString) => {
    const mins = parseTimeFromDateTime(dateTimeString);
    if (mins === null) return null;
    const h24 = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    const period = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const startTime = formatTime(component.start_date_time);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2.5 p-2.5 rounded border transition-colors ${
        isRace ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-center gap-1.5">
        <button
          {...attributes}
          {...listeners}
          className="p-0.5 rounded hover:bg-slate-200 cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-200 text-2xs font-medium text-slate-600">
          {component.event_order}
        </span>
      </div>

      {startTime && (
        <div className="flex-shrink-0 w-16 text-center">
          <div className="flex items-center gap-1 text-xs font-medium text-slate-700">
            <Clock className="w-3 h-3 text-slate-400" />
            {startTime}
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium text-slate-800 truncate">{component.title}</h4>
          {isRace && (
            <Badge variant="success" size="sm">
              {component.race_type_name || 'Race'}
            </Badge>
          )}
          {component.series_name && (
            <Badge variant="primary" size="sm">
              {component.series_name}
            </Badge>
          )}
          {isRace && component.total_laps > 0 && (
            <Badge variant="neutral" size="sm">
              {component.total_laps} laps
            </Badge>
          )}
        </div>
        {component.description && (
          <p className="text-2xs text-slate-500 truncate mt-0.5">{component.description}</p>
        )}
        {component.duration && (
          <span className="text-2xs text-slate-400">Duration: {component.duration}</span>
        )}
      </div>

      <div className="flex items-center gap-0.5">
        <button
          onClick={() => onEdit(component)}
          className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700"
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(component)}
          className="p-1 rounded hover:bg-red-100 text-slate-500 hover:text-red-600"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Helper to parse duration input to HH:MM:SS format
function parseDurationInput(input) {
  if (!input) return '';

  const trimmed = input.trim();

  // If it's already in HH:MM:SS format, return as-is
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // If it's in MM:SS format, add hours
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    return `00:${trimmed.padStart(5, '0')}`;
  }

  // If it's just a number, treat as minutes
  if (/^\d+$/.test(trimmed)) {
    const minutes = parseInt(trimmed, 10);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
  }

  // If it's a number with 'm' suffix (e.g., "15m"), treat as minutes
  if (/^\d+m$/i.test(trimmed)) {
    const minutes = parseInt(trimmed, 10);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
  }

  // If it's a number with 'h' suffix (e.g., "1h"), treat as hours
  if (/^\d+h$/i.test(trimmed)) {
    const hours = parseInt(trimmed, 10);
    return `${String(hours).padStart(2, '0')}:00:00`;
  }

  // Return as-is if we can't parse it
  return trimmed;
}

// Helper to display duration in friendly format
function formatDurationDisplay(duration) {
  if (!duration) return '';

  // Parse HH:MM:SS format
  const match = duration.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (!match) return duration;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours === 0 && minutes === 0) return '';
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}

// OOE Add/Edit Modal
const OOE_TITLE_PRESETS = [
  'Pill Draw',
  'Drivers Meeting',
  'Opening Ceremonies',
  'Engine Heat',
  'Hot Laps',
  'Qualifying',
  'Time Trials',
  'Heat 1',
  'Heat 2',
  'Heat 3',
  'Heat 4',
  'Heat 5',
  'Heat 6',
  'B-Main',
  'B-Main 1',
  'B-Main 2',
  'Last Chance Showdown',
  'Dash',
  'Feature',
  'A-Main',
  'Intermission',
  'Track Prep',
];

const isEngineHeatTitle = (title) => (title || '').trim().toLowerCase() === 'engine heat';

function OOEModal({ isOpen, onClose, onSave, component, raceTypes, series, existingComponents = [] }) {
  const durationToInput = (duration) => {
    if (!duration) return '';
    const match = duration.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
    if (!match) return duration;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours === 0) return String(minutes);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  };

  const calculateNextStartTime = () => {
    if (!existingComponents || existingComponents.length === 0) return '';

    const sortedComponents = [...existingComponents].sort((a, b) => b.event_order - a.event_order);
    const lastComponent = sortedComponents[0];

    if (!lastComponent.start_date_time) return '';

    const lastStartTime = new Date(lastComponent.start_date_time);

    const parseDurationToMinutes = (duration) => {
      if (!duration) return 15;
      if (!isNaN(duration)) return parseInt(duration, 10);
      const parts = duration.split(':').map(p => parseInt(p, 10));
      if (parts.length === 3) return parts[0] * 60 + parts[1];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
      return 15;
    };

    const durationMinutes = parseDurationToMinutes(lastComponent.duration);
    const nextStartTime = new Date(lastStartTime.getTime() + durationMinutes * 60 * 1000);
    const hours = String(nextStartTime.getHours()).padStart(2, '0');
    const minutes = String(nextStartTime.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState(() => {
    if (component) {
      let startTime = '';
      if (component.start_date_time) {
        const date = new Date(component.start_date_time);
        startTime = date.toTimeString().slice(0, 5);
      }
      return {
        title: component.title || '',
        start_time: startTime,
        race_type_ID: component.race_type_ID || '',
        series_id: component.series_id || '',
        duration: component.duration || '',
        description: component.description || '',
        total_laps: component.total_laps || ''
      };
    }
    return {
      title: '',
      start_time: calculateNextStartTime(),
      race_type_ID: '',
      series_id: '',
      duration: '',
      description: '',
      total_laps: ''
    };
  });
  const [durationInput, setDurationInput] = useState(() => component ? durationToInput(component.duration) : '');
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleDurationChange = (e) => {
    const value = e.target.value;
    setDurationInput(value);
    // Parse and store in HH:MM:SS format
    const parsed = parseDurationInput(value);
    setFormData({ ...formData, duration: parsed });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = isEngineHeatTitle(formData.title)
        ? { ...formData, race_type_ID: '', series_id: '', total_laps: '' }
        : formData;
      await onSave(payload);
      onClose();
    } catch (err) {
      console.error('Error saving component:', err);
    } finally {
      setSaving(false);
    }
  };

  // Show parsed duration preview
  const durationPreview = formData.duration ? formatDurationDisplay(formData.duration) : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-3 border-b border-slate-200">
          <h3 className="text-md font-semibold text-slate-800">
            {component ? 'Edit Component' : 'Add Component'}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3 space-y-3">
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => {
              const next = e.target.value;
              setFormData((prev) => ({
                ...prev,
                title: next,
                race_type_ID: isEngineHeatTitle(next) ? '' : prev.race_type_ID,
                series_id: isEngineHeatTitle(next) ? '' : prev.series_id,
                total_laps: isEngineHeatTitle(next) ? '' : prev.total_laps,
              }));
            }}
            placeholder="Type or pick a name"
            list="ooe-title-presets"
            required
          />
          <datalist id="ooe-title-presets">
            {OOE_TITLE_PRESETS.map((p) => <option key={p} value={p} />)}
          </datalist>

          <Input
            type="time"
            label="Start Time"
            value={formData.start_time}
            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
          />

          {!isEngineHeatTitle(formData.title) && (
            <Select
              label="Race Type (optional - makes this a race)"
              value={formData.race_type_ID}
              onChange={(e) => setFormData({ ...formData, race_type_ID: e.target.value })}
            >
              <option value="">Not a race</option>
              {raceTypes.map((rt) => (
                <option key={rt.race_type_ID} value={rt.race_type_ID}>
                  {rt.name}
                </option>
              ))}
            </Select>
          )}

          {formData.race_type_ID && !isEngineHeatTitle(formData.title) && series && series.length > 0 && (
            <Select
              label="Series (optional)"
              value={formData.series_id}
              onChange={(e) => setFormData({ ...formData, series_id: e.target.value })}
            >
              <option value="">No series</option>
              {series.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}

          {formData.race_type_ID && !isEngineHeatTitle(formData.title) && (
            <Input
              type="number"
              label="Total Laps"
              value={formData.total_laps}
              onChange={(e) => setFormData({ ...formData, total_laps: e.target.value })}
              placeholder="e.g., 25"
              min="1"
            />
          )}

          <div>
            <Input
              label="Duration"
              value={durationInput}
              onChange={handleDurationChange}
              placeholder="e.g., 15, 30, 1:30, 1h"
            />
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-slate-400">
                Enter minutes (15), hours (1h), or time (1:30)
              </p>
              {durationPreview && (
                <span className="text-xs text-emerald-600 font-medium">
                  = {durationPreview}
                </span>
              )}
            </div>
          </div>

          <Textarea
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Additional notes about this component"
            rows={2}
          />

          <div className="flex justify-end gap-1.5 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !formData.title}>
              {saving ? 'Saving...' : (component ? 'Update' : 'Add Component')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EventDetails() {
  const { seriesSlug, eventId } = useParams();
  const navigate = useNavigate();
  const api = useApi();

  const [localOoeComponents, setLocalOoeComponents] = useState(null);

  // OOE State
  const [showOoeModal, setShowOoeModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);

  // Radio channel state
  const [editingChannel, setEditingChannel] = useState(null);
  const [newChannel, setNewChannel] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);

  // Eloy Code copy state
  const [eloyCodeCopied, setEloyCodeCopied] = useState(false);

  // LTS Live Race connection
  const {
    status: ltsStatus,
    isConnected: ltsSocketConnected,
    ltsConnected,
    lastUpdate: ltsLastUpdate,
    isLoading: ltsLoading,
    subscribe: ltsSubscribe,
    unsubscribe: ltsUnsubscribe,
    refresh: ltsRefresh
  } = useLiveRace(eventId);

  const handleCopyEloyCode = async () => {
    try {
      await navigator.clipboard.writeText(eventId);
      setEloyCodeCopied(true);
      setTimeout(() => setEloyCodeCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const { data: event, isLoading: loading, error: eventError, refetch: refetchEvent } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const result = await api.events.getById(eventId);
      if (!result.success) throw new Error(result.message || 'Failed to fetch event');
      return result.data;
    },
    enabled: !!eventId,
  });
  const error = eventError?.message;

  const { data: eventStatuses = [], isLoading: statusesLoading } = useQuery({
    queryKey: ['event-statuses'],
    queryFn: async () => {
      const result = await api.apiCall('/api/events/data/statuses');
      if (Array.isArray(result)) return result;
      return result.data || [];
    },
  });

  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isLive = event?.event_status === 'Live';

  const handleStatusChange = async (statusId) => {
    setUpdatingStatus(true);
    setStatusMenuOpen(false);
    try {
      const result = await api.events.update(eventId, { track_promo_event_status_id: statusId });
      if (!result.success) {
        console.error('Failed to update event status:', result.message);
        alert(result.message || 'Failed to update event status');
        return;
      }
      refetchEvent();
    } catch (err) {
      console.error('Error updating event status:', err);
      alert('Failed to update event status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleGoLive = async () => {
    const inProgressStatus = eventStatuses.find(s => s.name === 'Live');
    if (inProgressStatus) {
      await handleStatusChange(inProgressStatus.track_promo_event_status_ID);
    }
  };


  const { data: queryOoeComponents = [], isLoading: ooeLoading, refetch: refetchOoe } = useQuery({
    queryKey: ['event-ooe', eventId],
    queryFn: async () => {
      const response = await api.apiCall(`/api/events/${eventId}/ooe`);
      if (Array.isArray(response)) return response;
      if (response.success) return response.data || [];
      return [];
    },
    enabled: !!eventId,
  });

  const ooeComponents = localOoeComponents ?? queryOoeComponents;
  const setOoeComponents = setLocalOoeComponents;

  const fetchOoeComponents = () => {
    setLocalOoeComponents(null);
    refetchOoe();
  };

  const { data: copySources = [] } = useQuery({
    queryKey: ['event-ooe-copy-sources', eventId],
    queryFn: async () => {
      const response = await api.apiCall(`/api/events/${eventId}/ooe/copy-sources`);
      if (Array.isArray(response)) return response;
      if (response.success) return response.data || [];
      return [];
    },
    enabled: !!eventId,
  });

  const { data: raceTypes = [] } = useQuery({
    queryKey: ['race-types'],
    queryFn: async () => {
      const response = await api.apiCall('/api/events/data/race-types');
      if (Array.isArray(response)) return response;
      if (response.success) return response.data || [];
      return [];
    },
  });

  // Radio channels
  const { data: radioChannels = [], refetch: refetchRadioChannels } = useQuery({
    queryKey: ['event-radio-channels', eventId],
    queryFn: async () => {
      const response = await api.apiCall(`/api/events/${eventId}/radio-channels`);
      if (Array.isArray(response)) return response;
      if (response.success) return response.data || [];
      return [];
    },
    enabled: !!eventId,
  });

  const CHANNEL_TYPES = [
    { id: 1, type: 'Driver' },
    { id: 2, type: 'Race Control' },
    { id: 3, type: 'DIRTVision Broadcast' },
  ];

  const handleSaveChannel = async (channelData, existingId) => {
    try {
      if (existingId) {
        await api.apiCall(`/api/events/${eventId}/radio-channels/${existingId}`, {
          method: 'PUT',
          data: channelData,
        });
      } else {
        await api.apiCall(`/api/events/${eventId}/radio-channels`, {
          method: 'POST',
          data: channelData,
        });
      }
      setEditingChannel(null);
      setNewChannel(null);
      refetchRadioChannels();
    } catch (err) {
      console.error('Error saving radio channel:', err);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    try {
      await api.apiCall(`/api/events/${eventId}/radio-channels/${channelId}`, {
        method: 'DELETE',
      });
      refetchRadioChannels();
    } catch (err) {
      console.error('Error deleting radio channel:', err);
    }
  };

  // Copy OOE from another event
  const handleCopyOoe = async (sourceEventId) => {
    setCopyLoading(true);
    try {
      await api.apiCall(`/api/events/${eventId}/ooe/copy-from/${sourceEventId}`, {
        method: 'POST'
      });
      fetchOoeComponents();
      setShowCopyModal(false);
    } catch (err) {
      console.error('Error copying OOE:', err);
    } finally {
      setCopyLoading(false);
    }
  };

  // OOE handlers
  const handleAddOoeComponent = () => {
    setEditingComponent(null);
    setShowOoeModal(true);
  };

  const handleEditOoeComponent = (component) => {
    setEditingComponent(component);
    setShowOoeModal(true);
  };

  const handleSaveOoeComponent = async (formData) => {
    const nextOrder = ooeComponents.length > 0
      ? Math.max(...ooeComponents.map(c => c.event_order)) + 1
      : 1;

    // Convert time input to datetime format for the database
    let startDateTime = null;
    if (formData.start_time && event?.event_date) {
      // Combine event date with the time
      const eventDate = new Date(event.event_date + 'T00:00:00').toISOString().split('T')[0];
      startDateTime = `${eventDate} ${formData.start_time}:00`;
    }

    const payload = {
      title: formData.title,
      start_date_time: startDateTime,
      race_type_ID: formData.race_type_ID ? parseInt(formData.race_type_ID, 10) : null,
      series_id: formData.series_id ? parseInt(formData.series_id, 10) : null,
      duration: formData.duration || null,
      description: formData.description || null,
      event_order: editingComponent ? editingComponent.event_order : nextOrder,
      total_laps: formData.total_laps ? parseInt(formData.total_laps, 10) : null
    };

    if (editingComponent) {
      // Update existing
      await api.apiCall(`/api/events/${eventId}/ooe/${editingComponent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: payload
      });
    } else {
      // Create new
      await api.apiCall(`/api/events/${eventId}/ooe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: payload
      });
    }

    // Refresh OOE components
    fetchOoeComponents();

    // If this component has a race type, also refresh event data to update the Races section
    if (payload.race_type_ID) {
      refetchEvent(); // Don't show loading state for background refresh
    }
  };

  const handleDeleteOoeComponent = async (component) => {
    if (!window.confirm(`Are you sure you want to delete "${component.title}"?`)) {
      return;
    }

    await api.apiCall(`/api/events/${eventId}/ooe/${component.id}`, {
      method: 'DELETE'
    });

    // Refresh OOE components
    fetchOoeComponents();

    // If deleted component was a race, also refresh event data to update the Races section
    if (component.race_type_ID) {
      refetchEvent();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ooeComponents.findIndex(c => c.id === active.id);
    const newIndex = ooeComponents.findIndex(c => c.id === over.id);
    const earliest = Math.min(oldIndex, newIndex);

    const originalTimes = ooeComponents.map(c => c.start_date_time);

    let reordered = arrayMove(ooeComponents, oldIndex, newIndex);
    reordered = reordered.map((c, i) => ({
      ...c,
      event_order: i + 1,
      start_date_time: i < earliest ? c.start_date_time : originalTimes[i],
    }));
    reordered = recalculateTimes(reordered, earliest + 1);

    setOoeComponents(reordered);

    await api.apiCall(`/api/events/${eventId}/ooe/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: {
        components: reordered.map(c => ({
          id: c.id,
          event_order: c.event_order,
          start_date_time: c.start_date_time,
        }))
      }
    });

    fetchOoeComponents();
  };

  // Print Order of Events
  const handlePrintOOE = () => {
    const formatTime = (dateTimeString) => {
      const mins = parseTimeFromDateTime(dateTimeString);
      if (mins === null) return '';
      const h24 = Math.floor(mins / 60) % 24;
      const m = mins % 60;
      const period = h24 >= 12 ? 'PM' : 'AM';
      const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    };

    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const safeColor = (value) => {
      let hex = String(value ?? '').trim().replace(/^#/, '');
      if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map(c => c + c).join('');
      return /^[0-9a-f]{6}$/i.test(hex) ? '#' + hex : '#eab308';
    };

    // Get unique series with logos for the header, deduped by logo URL
    // (DIRTcar divisions share one DIRTcar Racing logo)
    const seenLogoURLs = new Set();
    const seriesWithLogos = (event?.series || []).filter(s => {
      if (!s.series_logo_URL || seenLogoURLs.has(s.series_logo_URL)) return false;
      seenLogoURLs.add(s.series_logo_URL);
      return true;
    });

    // Milestones shown in the summary bar instead of the schedule. Exclusion is keyed to
    // the exact component rendered (by id), so duplicate or untimed rows stay in the schedule.
    // Match a milestone only when the title is the keyword alone or qualified by an "open"
    // variant, so rows like "Pill Draw Closes" or "Pit Gate Ends" stay in the schedule.
    const matchMilestone = (keyword) => {
      const word = new RegExp(`\\b${keyword}s?\\b`);
      return (t) => word.test(t) && /^(opens?|opened|opening)?$/.test(t.replace(word, '').replace(/[^a-z0-9]/g, ''));
    };
    const preEventMilestones = [
      { label: 'Pit Gate Open', match: matchMilestone('pit gate') },
      { label: 'Pill Draw Open', match: matchMilestone('pill draw') },
      { label: 'Drivers Meeting', match: matchMilestone('drivers meeting') },
      { label: 'Engine Heat', match: matchMilestone('engine heat') },
    ];
    const preEventBarIds = new Set();
    const preEventItems = preEventMilestones.map(({ label, match }) => {
      const comp = ooeComponents.find(c => {
        const t = c.title && c.title.toLowerCase();
        return t && match(t) && !preEventBarIds.has(c.id) && formatTime(c.start_date_time);
      });
      if (!comp) return '';
      preEventBarIds.add(comp.id);
      return `<div class="pre-event-item"><strong>${label}:</strong> ${formatTime(comp.start_date_time)}</div>`;
    }).filter(Boolean);
    const preEventBar = preEventItems.length > 0 ? `<div class="pre-event-bar">${preEventItems.join('')}</div>` : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order of Events - ${escapeHtml(event?.name || 'Event')}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800&family=Barlow:wght@400;500;600;700&display=swap');

          * { margin: 0; padding: 0; box-sizing: border-box; }

          body {
            font-family: 'Barlow', sans-serif;
            background: #fff;
            color: #111;
            line-height: 1.2;
            font-size: 14px;
          }

          .page {
            max-width: 100%;
            margin: 0;
            padding: 4px 8px;
          }

          /* Header */
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 0;
            border-bottom: 3px solid #333;
            margin-bottom: 4px;
          }

          .header-left {
            flex: 1;
          }

          .header-title {
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 42px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #333;
            line-height: 1;
          }

          .header-event {
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 24px;
            font-weight: 600;
            color: #555;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .header-logos {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .header-logos img {
            height: 45px;
            width: auto;
            object-fit: contain;
          }

          /* Event Info Bar */
          .event-bar {
            display: flex;
            gap: 40px;
            padding: 4px 0;
            border-bottom: 1px solid #ddd;
            margin-bottom: 4px;
          }

          .event-bar-item {
            font-size: 15px;
            color: #555;
          }

          .event-bar-item strong {
            font-weight: 700;
            color: #333;
          }

          /* Schedule */
          .schedule {
            width: 100%;
          }

          .schedule-header {
            display: flex;
            background: #444;
            color: #fff;
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .schedule-header > div {
            padding: 7px 12px;
          }

          .col-time { width: 100px; }
          .col-event { flex: 1; }

          .schedule-row {
            display: flex;
            align-items: center;
            border-bottom: 1px solid #e5e5e5;
          }

          .schedule-row:nth-child(even) {
            background: #f9f9f9;
          }

          .schedule-row.race {
            border-left: 4px solid #eab308;
          }

          .schedule-row.feature {
            color: #fff;
            border-left: 4px solid #eab308;
          }

          .schedule-row > div {
            padding: 7px 12px;
          }

          .row-time {
            font-size: 15px;
            font-weight: 600;
          }

          .row-event {
            font-size: 16px;
            font-weight: 600;
          }

          .row-laps {
            font-weight: 500;
            font-size: 13px;
            opacity: 0.7;
          }

          .row-series {
            font-size: 12px;
            color: #888;
            font-weight: 500;
          }

          .feature .row-series {
            color: rgba(255, 255, 255, 0.7);
          }

          /* Pre-event Info */
          .pre-event-bar {
            display: flex;
            gap: 40px;
            padding: 4px 0;
            border-bottom: 1px solid #ddd;
            margin-bottom: 4px;
          }

          .pre-event-item {
            font-size: 15px;
            color: #555;
          }

          .pre-event-item strong {
            font-weight: 700;
            color: #333;
          }

          .pre-event-item .time-blank {
            display: inline-block;
            min-width: 60px;
            border-bottom: 1px solid #999;
          }

          /* Tire Rules */
          .tire-rules {
            margin-top: 8px;
            padding: 6px 12px;
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 13px;
            color: #444;
            line-height: 1.5;
          }

          .tire-rules-title {
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 15px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #333;
            margin-bottom: 2px;
          }

          /* Radio Channels */
          .radio-channels {
            margin-top: 8px;
            padding: 6px 12px;
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 4px;
          }

          .radio-channels-title {
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 15px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #333;
            margin-bottom: 4px;
          }

          .radio-channels-grid {
            display: flex;
            gap: 24px;
            flex-wrap: wrap;
          }

          .radio-channel {
            display: flex;
            flex-direction: column;
          }

          .radio-label {
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            color: #888;
            letter-spacing: 0.5px;
          }

          .radio-freq {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }

          /* Sponsors */
          .sponsors {
            margin-top: 8px;
            padding-top: 6px;
            border-top: 1px solid #eee;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 20px;
            flex-wrap: wrap;
            opacity: 0.4;
          }

          .sponsors img {
            height: 28px;
            width: auto;
            object-fit: contain;
          }

          /* Footer */
          .footer {
            margin-top: 6px;
            padding-top: 4px;
            border-top: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            color: #666;
          }

          .footer-brand {
            font-weight: 700;
            color: #444;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .disclaimer {
            margin-top: 8px;
            text-align: center;
            font-size: 10px;
            font-style: italic;
            color: #888;
          }

          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .page { padding: 4px 8px; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <div class="header-left">
              <div class="header-title">Order of Events</div>
              <div class="header-event">${escapeHtml(event?.name || 'Event')}</div>
            </div>
            ${seriesWithLogos.length > 0 || event?.event_logo_url ? `
              <div class="header-logos">
                ${event?.event_logo_url ? `<img src="${escapeHtml(event.event_logo_url)}" alt="${escapeHtml(event.name)}" />` : ''}
                ${seriesWithLogos.map(s => `<img src="${escapeHtml(s.series_logo_URL)}" alt="${escapeHtml(s.name)}" />`).join('')}
              </div>
            ` : ''}
          </div>

          <div class="event-bar">
            <div class="event-bar-item"><strong>Date:</strong> ${event?.event_date ? new Date(event.event_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD'}</div>
            <div class="event-bar-item"><strong>Track:</strong> ${escapeHtml(event?.track_name || 'TBD')}</div>
            <div class="event-bar-item"><strong>Location:</strong> ${escapeHtml([event?.track_city, event?.state_code].filter(Boolean).join(', ') || 'TBD')}</div>
            ${event?.track_timezone ? `<div class="event-bar-item"><strong>Times:</strong> ${escapeHtml(shortTimezoneLabel(event.track_timezone, event.event_date) || event.track_timezone)}</div>` : ''}
          </div>

          ${preEventBar}

          <div class="schedule">
            <div class="schedule-header">
              <div class="col-time">Time</div>
              <div class="col-event">Event</div>
            </div>
            ${ooeComponents.filter(comp => !preEventBarIds.has(comp.id)).map((comp, idx) => {
              const isRace = !!comp.race_type_ID;
              const isFeature = comp.race_type_name && (comp.race_type_name.toLowerCase().includes('feature') || comp.race_type_name.toLowerCase().includes('a-main'));
              const primaryColor = safeColor(comp.series_primary_color);
              const bgColor = isFeature ? primaryColor : (isRace ? primaryColor + '18' : '');
              const borderColor = isRace ? primaryColor : '';
              const textColor = isFeature ? '#fff' : '';
              return `
                <div class="schedule-row ${isRace ? (isFeature ? 'feature' : 'race') : ''}" style="${isRace ? `border-left-color: ${borderColor};` : ''} ${bgColor ? `background: ${bgColor};` : ''} ${textColor ? `color: ${textColor};` : ''}">
                  <div class="col-time row-time">
                    ${idx === 0 ? (formatTime(comp.start_date_time) || '-') : ''}
                  </div>
                  <div class="col-event">
                    <div class="row-event">${escapeHtml(comp.title)}${isRace && comp.total_laps > 0 ? ` <span class="row-laps">(${comp.total_laps} laps)</span>` : ''}</div>
                    ${comp.series_name ? `<div class="row-series">${escapeHtml(comp.series_name)}</div>` : ''}
                  </div>
                </div>
              `;
            }).join('')}
          </div>

          ${(() => {
            if (!event?.tire_rules) return '';
            const plainText = event.tire_rules
              .replace(/<br\s*\/?>/gi, '\n')
              .replace(/<\/p>/gi, '\n')
              .replace(/<[^>]+>/g, '')
              .replace(/&amp;/g, '&')
              .replace(/&nbsp;/g, ' ')
              .replace(/\*\*/g, '')
              .split('\n')
              .map(l => l.trim())
              .filter(Boolean);
            return '<div class="tire-rules"><div class="tire-rules-title">Tire Rules</div>' + plainText.map(l => '<div>' + escapeHtml(l) + '</div>').join('') + '</div>';
          })()}

          ${(() => {
            if (!radioChannels || radioChannels.length === 0) return '';
            return '<div class="radio-channels"><div class="radio-channels-title">Radio Frequencies</div><div class="radio-channels-grid">' +
              radioChannels.map(ch => '<div class="radio-channel"><span class="radio-label">' + escapeHtml(ch.channel_type) + '</span><span class="radio-freq">' + (ch.channel ? 'Ch ' + escapeHtml(ch.channel) + (ch.frequency ? ' / ' : '') : '') + (ch.frequency ? escapeHtml(ch.frequency) + ' MHz' : '') + '</span></div>').join('') +
              '</div></div>';
          })()}

          ${(() => {
            const sponsors = (event?.sponsors || []).filter(s => s.logo_url);
            if (sponsors.length === 0) return '';
            return '<div class="sponsors">' + sponsors.map(s => '<img src="' + escapeHtml(s.logo_url) + '" alt="' + escapeHtml(s.sponsor_name) + '" />').join('') + '</div>';
          })()}

          <div class="footer">
            <div>Generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            <div class="footer-brand">World Racing Group</div>
          </div>

          <div class="disclaimer">
            ***This is a provisional schedule and is subject to change at any time without notice. All times are estimates.***
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Unable to open the print window. Check that pop-ups are allowed for this site.');
      return;
    }
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-slate-200 rounded w-1/3" />
        <div className="h-4 bg-slate-200 rounded w-1/2" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <Card padding="compact"><CardContent className="p-4"><div className="h-32 bg-slate-100 rounded" /></CardContent></Card>
            <Card padding="compact"><CardContent className="p-4"><div className="h-48 bg-slate-100 rounded" /></CardContent></Card>
          </div>
          <div className="space-y-4">
            <Card padding="compact"><CardContent className="p-4"><div className="h-32 bg-slate-100 rounded" /></CardContent></Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-slate-800 mb-1.5">Error Loading Event</h2>
        <p className="text-xs text-slate-500 mb-3">{error}</p>
        <Button size="sm" onClick={() => navigate(`/${seriesSlug}/events`)}>
          Back to Events
        </Button>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-slate-800 mb-1.5">Event Not Found</h2>
        <Button size="sm" onClick={() => navigate(`/${seriesSlug}/events`)}>
          Back to Events
        </Button>
      </div>
    );
  }

  const statusInfo = eventStatusConfig[event.event_status] || eventStatusConfig['Scheduled'];

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb & Header */}
      <div className="mb-4">
        <Link
          to={`/${seriesSlug}/events`}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-3"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to Events
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-lg font-semibold text-slate-800">{event.name}</h1>
              <Badge variant={statusInfo.variant} size="sm">
                {event.event_status || 'Scheduled'}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(event.event_date)}
              </span>
              {event.start_time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {formatTime12Hour(event.start_time)}
                  {event.track_timezone && (
                    <span className="text-slate-400">
                      {shortTimezoneLabel(event.track_timezone, event.event_date) || event.track_timezone}
                    </span>
                  )}
                </span>
              )}
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {event.track_name}
                {event.track_city && event.state_code && (
                  <span className="text-slate-400">
                    ({event.track_city}, {event.state_code})
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <Link to={`/${seriesSlug}/events/${eventId}/edit`}>
              <Button variant="ghost" size="xs" leftIcon={Edit}>
                Edit
              </Button>
            </Link>
            <Button variant="ghost" size="xs" leftIcon={MoreVertical} />
          </div>
        </div>
      </div>

      {/* Missing start time warning */}
      {!event.start_time && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-700">No start time set for this event.</span>
          <Link
            to={`/${seriesSlug}/events/${eventId}/edit`}
            className="ml-auto text-sm font-medium text-amber-800 hover:text-amber-900 underline"
          >
            Add start time
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Quick Actions */}
          <Card padding="compact">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <QuickAction
                  icon={ClipboardList}
                  label="Manage Entries"
                  to={`/${seriesSlug}/events/${eventId}/entries`}
                  variant="primary"
                />
                <QuickAction
                  icon={Send}
                  label="Send Lineups"
                  onClick={() => alert('Send lineups feature coming soon')}
                />
                <QuickAction
                  icon={Trophy}
                  label="Enter Results"
                  to={`/${seriesSlug}/events/${eventId}/results`}
                />
                <div className="relative" onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setStatusMenuOpen(false);
                  }
                }}>
                  {isLive ? (
                    <>
                      <button
                        onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                        disabled={updatingStatus}
                        className="w-full flex flex-col items-center gap-1.5 p-3 rounded border transition-colors cursor-pointer bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
                      >
                        <Flag className="w-5 h-5" />
                        <span className="text-xs font-medium text-center flex items-center gap-1">
                          {updatingStatus ? 'Updating...' : 'End Event'}
                          <ChevronDown className="w-3 h-3" />
                        </span>
                      </button>
                      {statusMenuOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg z-20">
                          {eventStatuses
                            .filter(s => s.name !== 'Live')
                            .map(status => (
                              <button
                                key={status.track_promo_event_status_ID}
                                onClick={() => handleStatusChange(status.track_promo_event_status_ID)}
                                className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 first:rounded-t last:rounded-b"
                              >
                                {status.name}
                              </button>
                            ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={handleGoLive}
                      disabled={updatingStatus || statusesLoading}
                      className="w-full flex flex-col items-center gap-1.5 p-3 rounded border transition-colors cursor-pointer bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Play className="w-5 h-5" />
                      <span className="text-xs font-medium text-center">
                        {updatingStatus ? 'Updating...' : 'Go Live'}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Order of Events (OOE) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Order of Events</CardTitle>
                  <CardDescription>
                    {ooeComponents.length} components scheduled
                    {event?.track_timezone && (
                      <>
                        {' · all times '}
                        <span className="font-medium text-slate-600">
                          {shortTimezoneLabel(event.track_timezone, event.event_date) || event.track_timezone}
                        </span>
                      </>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {ooeComponents.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={Printer}
                      onClick={handlePrintOOE}
                      title="Print order of events"
                    >
                      Print
                    </Button>
                  )}
                  {copySources.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={Copy}
                      onClick={() => setShowCopyModal(true)}
                      title="Copy order of events from another day"
                    >
                      Copy
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={Plus}
                    onClick={handleAddOoeComponent}
                  >
                    Add Component
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {ooeLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : ooeComponents.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={ooeComponents.map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {ooeComponents.map((component) => (
                        <SortableOOEItem
                          key={component.id}
                          component={component}
                          onEdit={handleEditOoeComponent}
                          onDelete={handleDeleteOoeComponent}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="text-center py-8">
                  <List className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No components scheduled yet</p>
                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={Plus}
                      onClick={handleAddOoeComponent}
                    >
                      Add First Component
                    </Button>
                    {copySources.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={Copy}
                        onClick={() => setShowCopyModal(true)}
                      >
                        Copy from Another Day
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* OOE Modal */}
          <OOEModal
            key={editingComponent?.id ?? 'new'}
            isOpen={showOoeModal}
            onClose={() => setShowOoeModal(false)}
            onSave={handleSaveOoeComponent}
            component={editingComponent}
            raceTypes={raceTypes}
            series={event?.series || []}
            existingComponents={ooeComponents}
          />

          {/* Copy OOE Modal */}
          {showCopyModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCopyModal(false)}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold">Copy Order of Events</h3>
                  <button onClick={() => setShowCopyModal(false)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  <p className="text-sm text-slate-500 mb-4">Select a day to copy the order of events from:</p>
                  <div className="space-y-2">
                    {copySources.map(source => (
                      <button
                        key={source.id}
                        className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50"
                        disabled={copyLoading}
                        onClick={() => handleCopyOoe(source.id)}
                      >
                        <div className="font-medium text-slate-800">{source.name}</div>
                        <div className="text-sm text-slate-500">
                          {source.event_date ? new Date(source.event_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}
                          <span className="ml-2 text-slate-400">{source.component_count} components</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Series */}
          {event.series && event.series.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Series Racing</CardTitle>
                <CardDescription>{event.series.length} series at this event</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {event.series.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"
                      style={s.primary_HTML_color ? {
                        borderLeft: `3px solid ${s.primary_HTML_color}`
                      } : {}}
                    >
                      {s.series_logo_URL && (
                        <img
                          src={s.series_logo_URL}
                          alt={s.name}
                          className="w-6 h-6 object-contain"
                        />
                      )}
                      <span className="text-sm font-medium text-slate-700">{s.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Races */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Races</CardTitle>
                  <CardDescription>
                    {event.races?.length || 0} races scheduled
                  </CardDescription>
                </div>
                {/* Add Race button - disabled until RaceForm page is built */}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={Plus}
                  disabled
                  title="Add race feature coming soon"
                >
                  Add Race
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {event.races && event.races.length > 0 ? (
                <div className="space-y-2">
                  {event.races.map((race) => (
                    <RaceCard
                      key={race.id}
                      race={race}
                      seriesSlug={seriesSlug}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Flag className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No races created yet</p>
                  {/* Create First Race button - disabled until RaceForm page is built */}
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={Plus}
                    disabled
                    title="Add race feature coming soon"
                  >
                    Create First Race
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Eloy Code */}
          <Card className="border-wrg-blue-600/30 bg-gradient-to-br from-wrg-blue-600/5 to-transparent">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <img
                  src="/images/ELOY_STICKER.png"
                  alt="Eloy"
                  className="w-10 h-10 object-contain"
                />
                <div>
                  <CardTitle className="text-sm">Eloy Code</CardTitle>
                  <CardDescription className="text-xs">
                    Enter this code in Live Timing Relay to sync data
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-3 font-mono text-xl font-bold text-slate-800 text-center">
                  {eventId}
                </div>
                <button
                  onClick={handleCopyEloyCode}
                  className={`p-3 rounded-lg border transition-all ${
                    eloyCodeCopied
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                      : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }`}
                  title={eloyCodeCopied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {eloyCodeCopied ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* LTS Connection Status */}
              <div className="border-t border-slate-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Live Timing Status
                  </span>
                  <button
                    onClick={ltsRefresh}
                    disabled={ltsLoading}
                    className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 disabled:opacity-50"
                    title="Refresh data from LTS"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${ltsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Socket Connection */}
                  <div className="flex items-center gap-2">
                    {ltsSocketConnected ? (
                      <Wifi className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-slate-400" />
                    )}
                    <span className={`text-xs ${ltsSocketConnected ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {ltsSocketConnected ? 'Connected to RMS' : 'Disconnected from RMS'}
                    </span>
                  </div>

                  {/* Eloy Connection Status */}
                  <div className="flex items-center gap-2">
                    {ltsStatus?.eloy_status === 'connected' ? (
                      <>
                        <Radio className="w-4 h-4 text-emerald-500 animate-pulse" />
                        <span className="text-xs text-emerald-600">Eloy Connected</span>
                      </>
                    ) : (
                      <>
                        <Radio className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-500">Eloy Disconnected</span>
                      </>
                    )}
                  </div>

                  {/* Race Info (if connected and has status) */}
                  {ltsStatus?.eloy_status === 'connected' && (
                    <div className="mt-2 p-2 bg-slate-50 rounded-md space-y-1.5">
                      {ltsStatus.race_name && (
                        <div className="text-xs font-medium text-slate-700">
                          {ltsStatus.race_name}
                        </div>
                      )}
                      {ltsStatus.class_name && (
                        <div className="text-xs text-slate-500">
                          Class: {ltsStatus.class_name}
                        </div>
                      )}
                      {ltsStatus.flag_status && (
                        <div className="flex items-center gap-2">
                          <Flag className={`w-3.5 h-3.5 ${
                            ltsStatus.flag_status.trim().toUpperCase() === 'GREEN' ? 'text-emerald-500' :
                            ltsStatus.flag_status.trim().toUpperCase() === 'YELLOW' || ltsStatus.flag_status.trim().toUpperCase() === 'CAUTION' ? 'text-amber-500' :
                            ltsStatus.flag_status.trim().toUpperCase() === 'RED' ? 'text-red-500' :
                            ltsStatus.flag_status.trim().toUpperCase() === 'CHECKERED' ? 'text-slate-800' :
                            'text-slate-500'
                          }`} />
                          <span className="text-xs text-slate-600">
                            {ltsStatus.flag_status.trim()} Flag
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {ltsStatus.lead_lap && (
                          <span>Lap {ltsStatus.lead_lap}</span>
                        )}
                        {ltsStatus.laps_to_go && ltsStatus.laps_to_go !== '0' && (
                          <span>• {ltsStatus.laps_to_go} to go</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Last Seen */}
                  {ltsStatus?.eloy_last_seen && (
                    <div className="text-xs text-slate-400 pt-1">
                      Last seen: {new Date(ltsStatus.eloy_last_seen).toLocaleTimeString()}
                    </div>
                  )}

                  {/* Subscribe Button (if eloy not connected) */}
                  {ltsStatus?.eloy_status !== 'connected' && ltsSocketConnected && (
                    <button
                      onClick={ltsSubscribe}
                      disabled={ltsLoading}
                      className="w-full mt-2 px-3 py-1.5 bg-wrg-blue-600/10 hover:bg-wrg-blue-600/20 text-wrg-blue-600 text-xs font-medium rounded-md transition-colors disabled:opacity-50"
                    >
                      {ltsLoading ? 'Connecting...' : 'Connect to LTS Feed'}
                    </button>
                  )}

                  {/* Disconnect Button (if eloy connected) */}
                  {ltsStatus?.eloy_status === 'connected' && (
                    <button
                      onClick={ltsUnsubscribe}
                      disabled={ltsLoading}
                      className="w-full mt-2 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-md transition-colors disabled:opacity-50"
                    >
                      {ltsLoading ? 'Disconnecting...' : 'Disconnect from LTS'}
                    </button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Event Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Event Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-wrg-blue-600" />
                    <span className="text-sm text-slate-600">Total Entries</span>
                  </div>
                  <span className="text-lg font-semibold text-slate-800">
                    {event.total_entry_count || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Flag className="w-5 h-5 text-emerald-500" />
                    <span className="text-sm text-slate-600">Total Races</span>
                  </div>
                  <span className="text-lg font-semibold text-slate-800">
                    {event.total_races_count || event.races?.length || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Timer className="w-5 h-5 text-amber-500" />
                    <span className="text-sm text-slate-600">Race Events</span>
                  </div>
                  <span className="text-lg font-semibold text-slate-800">
                    {event.total_race_events_count || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Track Info */}
          <Card>
            <CardHeader>
              <CardTitle>Track Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Track</p>
                  <p className="font-medium text-slate-800">{event.track_name}</p>
                </div>
                {(event.track_city || event.state_code) && (
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-medium text-slate-800">
                      {[event.track_city, event.state_code].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
                {event.length_miles && (
                  <div>
                    <p className="text-sm text-slate-500">Track Length</p>
                    <p className="font-medium text-slate-800">{parseFloat(event.length_miles).toFixed(3)} miles</p>
                  </div>
                )}
                {event.track_type && (
                  <div>
                    <p className="text-sm text-slate-500">Surface</p>
                    <p className="font-medium text-slate-800">{event.track_type}</p>
                  </div>
                )}
                {/* Track Details link - disabled until TrackDetails page is built */}
                {event.track_ID && (
                  <span
                    className="inline-flex items-center gap-1 text-sm text-slate-400 mt-2 cursor-not-allowed"
                    title="Track details page coming soon"
                  >
                    View Track Details
                    <ExternalLink className="w-3 h-3" />
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Radio Channels */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Radio Channels</CardTitle>
                {!newChannel && (
                  <Button
                    variant="ghost"
                    size="xs"
                    leftIcon={Plus}
                    onClick={() => setNewChannel({ radio_channel_type_ID: 1, channel: '', frequency: '' })}
                  >
                    Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {radioChannels.map((ch) => (
                  editingChannel === ch.id ? (
                    <div key={ch.id} className="space-y-2 p-2 bg-slate-50 rounded border border-slate-200">
                      <Select
                        size="sm"
                        value={ch._editType || ch.radio_channel_type_ID}
                        onChange={(e) => {
                          ch._editType = parseInt(e.target.value);
                        }}
                        id={`ch-type-${ch.id}`}
                      >
                        {CHANNEL_TYPES.map(t => <option key={t.id} value={t.id}>{t.type}</option>)}
                      </Select>
                      <div className="flex gap-2">
                        <Input size="sm" placeholder="Ch #" defaultValue={ch.channel || ''} id={`ch-num-${ch.id}`} className="w-16" />
                        <Input size="sm" placeholder="Frequency" defaultValue={ch.frequency || ''} id={`ch-freq-${ch.id}`} className="flex-1" />
                      </div>
                      <div className="flex gap-1">
                        <Button size="xs" onClick={() => {
                          const typeEl = document.getElementById(`ch-type-${ch.id}`);
                          const numEl = document.getElementById(`ch-num-${ch.id}`);
                          const freqEl = document.getElementById(`ch-freq-${ch.id}`);
                          handleSaveChannel({
                            radio_channel_type_ID: parseInt(typeEl.value),
                            channel: numEl.value ? parseInt(numEl.value) : null,
                            frequency: freqEl.value || null,
                          }, ch.id);
                        }}>Save</Button>
                        <Button variant="ghost" size="xs" onClick={() => setEditingChannel(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div key={ch.id} className="flex items-center justify-between p-2 rounded bg-slate-50 border border-slate-200 group">
                      <div>
                        <div className="text-xs font-medium text-slate-700">{ch.channel_type}</div>
                        <div className="text-xs text-slate-500">
                          {ch.channel ? `Ch ${ch.channel}` : ''}{ch.channel && ch.frequency ? ' / ' : ''}{ch.frequency ? `${ch.frequency} MHz` : ''}
                        </div>
                      </div>
                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="xs" onClick={() => setEditingChannel(ch.id)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => handleDeleteChannel(ch.id)}>
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  )
                ))}
                {newChannel && (
                  <div className="space-y-2 p-2 bg-slate-50 rounded border border-slate-200">
                    <Select size="sm" id="new-ch-type" defaultValue="1">
                      {CHANNEL_TYPES.map(t => <option key={t.id} value={t.id}>{t.type}</option>)}
                    </Select>
                    <div className="flex gap-2">
                      <Input size="sm" placeholder="Ch #" id="new-ch-num" className="w-16" />
                      <Input size="sm" placeholder="Frequency" id="new-ch-freq" className="flex-1" />
                    </div>
                    <div className="flex gap-1">
                      <Button size="xs" onClick={() => {
                        handleSaveChannel({
                          radio_channel_type_ID: parseInt(document.getElementById('new-ch-type').value),
                          channel: document.getElementById('new-ch-num').value ? parseInt(document.getElementById('new-ch-num').value) : null,
                          frequency: document.getElementById('new-ch-freq').value || null,
                        });
                      }}>Save</Button>
                      <Button variant="ghost" size="xs" onClick={() => setNewChannel(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
                {radioChannels.length === 0 && !newChannel && (
                  <p className="text-xs text-slate-400 text-center py-2">No radio channels configured</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Event Times */}
          {(event.early_access_time || event.ready_meeting_time) && (
            <Card>
              <CardHeader>
                <CardTitle>Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {event.early_access_time && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Early Access</span>
                      <span className="text-sm font-medium text-slate-800">
                        {event.early_access_time}
                      </span>
                    </div>
                  )}
                  {event.ready_meeting_time && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Ready Meeting</span>
                      <span className="text-sm font-medium text-slate-800">
                        {event.ready_meeting_time}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {event.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{event.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Ticket Links */}
          {event.tickets?.some(t => /^https?:\/\//i.test(t.link_URL || '')) && (
            <div className="space-y-2">
              {event.tickets.filter(t => /^https?:\/\//i.test(t.link_URL || '')).map(ticket => (
                <a
                  key={ticket.event_ticket_ID}
                  href={ticket.link_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="secondary" className="w-full" rightIcon={ExternalLink}>
                    {ticket.name ? `Tickets: ${ticket.name}` : 'View Tickets'}
                  </Button>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
